import React from 'react';
import { useSelector } from 'react-redux';
import { generatePath, Navigate, useParams } from 'react-router-dom';
import { FeatureToggle, PageNotFound } from '@components/common';
import ViewerAccessGate from '@components/common/ViewerAccessGate';
import { useDefaultLandingPageStore } from '@globalStore/defaultLandingPageStore';
import { useInitialDefaultLandingPage } from '@hooks/useDefaultLandingPage';
import {
  Main,
  CustomDashboard,
  WithResetTrends,
  WithPersistence,
  WithModifyComparison,
  AppExperience,
  DPIOverviewDashboard,
  TempAppExperienceDrilldown,
  WebhooksListPage,
  WebhooksEditPage,
  ErrorDiagnosticsSharing,
  TempDashboardErrorDiagnostics,
  DashboardErrorDiagnostics,
  TempTimeline,
  TimelineSharing,
  SavedTimeline,
  NocContainer,
  MLContainer,
  NocSharing,
  DashboardNoc,
  TempDashboardNoc,
  NocSavedDashboard,
  AiAlertsDashboard,
  ViewerModule,
  OverviewDashboard,
  AppTimeline,
  AppTimelineShare,
  ResiliencyDashboard,
  AIAlertCohorts,
  AppTimelineInit,
} from '../pages';
import { isAppModule, isOdpModule, PAGE_TYPE } from '@utils/constants';
import featureNames from '@utils/featureToggle/constants';
import NocFeatureCheck from '@src/pages/Noc/NocFeatureCheck';
import { isCustomerServiceRoleSelector } from '@store/common/selectors';
import { homePagePath, singleAppPrefix } from './utils';
import useCanAccessFeature from '@hooks/useCanAccessFeature';
const { baseUrl } = window.conviva;
import Nexa from '@src/pages/Nexa';

export const RedirectToHomePage = () => {
  const defaultPath = useDefaultLandingPageStore((state) => state.path);
  const { isLoading } = useInitialDefaultLandingPage();

  // Show loading while fetching default landing page data
  if (isLoading) {
    return <div>Redirecting...</div>;
  }

  let actualPath = '';
  if (defaultPath) {
    actualPath = defaultPath.startsWith(singleAppPrefix)
      ? defaultPath
      : `${singleAppPrefix}${defaultPath}`;
  } else {
    if (isAppModule) {
      actualPath = `${homePagePath}/dashboard/overview`;
    } else if (isOdpModule) {
      // we will most likely be asked to change the landing route
      actualPath = `${homePagePath}/dashboard/explore`;
    } else {
      actualPath = `${homePagePath}/dashboard/trends`;
    }
  }
  return <Navigate to={actualPath} replace />;
};

export const TrendsPage = (props) => {
  const canAccess = useCanAccessFeature({ feature: 'trends' });
  const isCustomerService = useSelector(isCustomerServiceRoleSelector);

  if (isAppModule) {
    return <Navigate to={`${baseUrl}/dashboard/app-experience`} replace />;
  }

  if (!canAccess) {
    // workaround for customer service role access restrictions
    if (isCustomerService) {
      return <Navigate to={`${baseUrl}/dashboard/viewer-module`} replace />;
    }
    return null;
  }

  return (
    <WithResetTrends>
      <WithPersistence>
        <Main {...props} />
      </WithPersistence>
    </WithResetTrends>
  );
};

export const AppExperienceDashboardPage = (props) => {
  return (
    <FeatureToggle feature={featureNames.APP_EXPERIENCE} fallback={<PageNotFound />} checkRole>
      <WithResetTrends>
        <WithPersistence>
          <AppExperience {...props} />
        </WithPersistence>
      </WithResetTrends>
    </FeatureToggle>
  );
};

export const DPIOverviewDashboardPage = (props) => {
  return (
    <FeatureToggle feature={featureNames.APP_EXPERIENCE} fallback={<PageNotFound />} checkRole>
      <DPIOverviewDashboard {...props} />
    </FeatureToggle>
  );
};

export const AppWebhooksListPage = () => {
  return (
    <FeatureToggle feature={featureNames.APP_AI_ALERTS} fallback={<PageNotFound />} checkRole>
      <WebhooksListPage />
    </FeatureToggle>
  );
};

export const AppWebhooksEditPage = (props) => {
  return (
    <FeatureToggle feature={featureNames.APP_AI_ALERTS} fallback={<PageNotFound />} checkRole>
      <WebhooksEditPage {...props} />
    </FeatureToggle>
  );
};

export const AppExperienceComparisonPage = (props) => {
  return (
    <FeatureToggle feature={featureNames.APP_EXPERIENCE} fallback={<PageNotFound />} checkRole>
      <CustomDashboard {...props} />
    </FeatureToggle>
  );
};

export const AppExperienceComparisonDrillDownPage = (props) => {
  return (
    <FeatureToggle feature={featureNames.APP_EXPERIENCE} fallback={<PageNotFound />} checkRole>
      <WithModifyComparison {...props}>
        <AppExperience />
      </WithModifyComparison>
    </FeatureToggle>
  );
};

export const AppExperienceTempDrilldownPage = (props) => {
  return (
    <FeatureToggle feature={featureNames.APP_EXPERIENCE} fallback={<PageNotFound />} checkRole>
      <TempAppExperienceDrilldown {...props} />
    </FeatureToggle>
  );
};

export const AppTimelinePage = (props) => {
  return (
    <FeatureToggle feature={featureNames.APP_USER_TIMELINE} fallback={<PageNotFound />} checkRole>
      <ViewerAccessGate>
        <AppTimeline {...props} />
      </ViewerAccessGate>
    </FeatureToggle>
  );
};

export const AppTimelineSharePage = (props) => {
  return (
    <FeatureToggle feature={featureNames.APP_USER_TIMELINE} fallback={<PageNotFound />} checkRole>
      <ViewerAccessGate>
        <AppTimelineShare {...props} />
      </ViewerAccessGate>
    </FeatureToggle>
  );
};

export const AppTimelineInitPage = (props) => {
  return (
    <FeatureToggle feature={featureNames.APP_USER_TIMELINE} fallback={<PageNotFound />} checkRole>
      <ViewerAccessGate>
        <AppTimelineInit {...props} />
      </ViewerAccessGate>
    </FeatureToggle>
  );
};

export const AppAiAlertsPage = (props) => (
  <FeatureToggle feature={featureNames.APP_AI_ALERTS} fallback={<PageNotFound />} checkRole>
    <AiAlertsDashboard {...props} />
  </FeatureToggle>
);

// error-diagnostics sharing feature toggle
export const ErrorDiagnosticsSharingRoute = (props) => (
  <FeatureToggle
    feature={featureNames.ERROR_DIAGNOSTICS}
    fallback={<Navigate to={homePagePath} replace />}
    checkRole
  >
    <ErrorDiagnosticsSharing {...props} />
  </FeatureToggle>
);

// error-diagnostics sharing feature toggle
export const TempDashboardErrorDiagnosticsRoute = (props) => (
  <FeatureToggle
    feature={featureNames.ERROR_DIAGNOSTICS}
    fallback={<Navigate to={homePagePath} replace />}
    checkRole
  >
    <TempDashboardErrorDiagnostics {...props} />
  </FeatureToggle>
);

export const DashboardErrorDiagnosticsRoute = (props) => (
  <FeatureToggle
    feature={featureNames.ERROR_DIAGNOSTICS}
    fallback={<Navigate to={homePagePath} replace />}
    checkRole
  >
    <DashboardErrorDiagnostics {...props} />
  </FeatureToggle>
);

export const TempTimelineRoute = (props) => (
  <FeatureToggle
    feature={featureNames.VIEWER_DRILLDOWN_TIMELINE}
    fallback={<Navigate to={homePagePath} replace />}
  >
    <ViewerAccessGate>
      <TempTimeline {...props} />
    </ViewerAccessGate>
  </FeatureToggle>
);

export const TimelineSharingRoute = (props) => (
  <FeatureToggle
    feature={featureNames.VIEWER_DRILLDOWN_TIMELINE}
    fallback={<Navigate to={homePagePath} replace />}
  >
    <ViewerAccessGate>
      <TimelineSharing {...props} />
    </ViewerAccessGate>
  </FeatureToggle>
);

export const SavedTimelineRoute = (props) => (
  <FeatureToggle
    feature={featureNames.VIEWER_DRILLDOWN_TIMELINE}
    fallback={<Navigate to={homePagePath} replace />}
  >
    <ViewerAccessGate>
      <SavedTimeline {...props} />
    </ViewerAccessGate>
  </FeatureToggle>
);

export const ViewerModulePage = (props) => (
  <FeatureToggle
    feature={featureNames.VIEWER_MODULE_PULSE5}
    fallback={<Navigate to={homePagePath} replace />}
  >
    <ViewerAccessGate>
      <WithPersistence>
        <ViewerModule {...props} />
      </WithPersistence>
    </ViewerAccessGate>
  </FeatureToggle>
);

export const NocPage = (props) => (
  <FeatureToggle feature={featureNames.NOC} fallback={<NocFeatureCheck {...props} />} checkRole>
    <WithPersistence>
      <NocContainer {...props} />
    </WithPersistence>
  </FeatureToggle>
);

export const NocSharingRoute = (props) => (
  <FeatureToggle
    feature={featureNames.NOC}
    fallback={<NocFeatureCheck {...props} pageType={PAGE_TYPE.SHARING} />}
    checkRole
  >
    <NocSharing {...props} />
  </FeatureToggle>
);

export const NocSavedDashboardBasedOnFF = (props) => (
  <FeatureToggle
    feature={featureNames.REALTIME_PSD}
    fallback={<DashboardNoc {...props} />}
    checkRole
  >
    <NocSavedDashboard {...props} />
  </FeatureToggle>
);

export const DashboardNocRoute = (props) => (
  <FeatureToggle
    feature={featureNames.NOC}
    fallback={<NocFeatureCheck {...props} pageType={PAGE_TYPE.SAVED_DASHBOARD} />}
    checkRole
  >
    <NocSavedDashboardBasedOnFF {...props} />
  </FeatureToggle>
);

export const TempDashboardNocRoute = (props) => (
  <FeatureToggle
    feature={featureNames.NOC}
    fallback={<NocFeatureCheck {...props} pageType={PAGE_TYPE.TEMP_DASHBOARD} />}
    checkRole
  >
    <TempDashboardNoc {...props} />
  </FeatureToggle>
);

export const MetricLensPage = (props) => (
  <FeatureToggle
    feature={featureNames.ML}
    fallback={<Navigate to={homePagePath} replace />}
    checkRole
  >
    <WithPersistence>
      <MLContainer {...props} />
    </WithPersistence>
  </FeatureToggle>
);

export const ExperienceDashboardPage = (props) => (
  <FeatureToggle
    feature={featureNames.EXPERIENCE_DASHBOARD}
    fallback={<Navigate to={`${baseUrl}/dashboard/trends`} replace />}
    checkRole
  >
    <WithPersistence>
      <OverviewDashboard {...props} />
    </WithPersistence>
  </FeatureToggle>
);

export const ResiliencyPolicyPage = () => <ResiliencyDashboard />;

export const AiAlertsPage = (props) => (
  <FeatureToggle
    feature={featureNames.AI_ALERTS || featureNames.IS_AI_ALERT_FORWARDING_PARTNER}
    fallback={<Navigate to={homePagePath} replace />}
    checkRole
  >
    <AiAlertsDashboard {...props} />
  </FeatureToggle>
);

export const VideoAIAlertsCohorts = () => (
  <FeatureToggle
    feature={featureNames.AI_ALERTS}
    fallback={<Navigate to={homePagePath} replace />}
    checkRole
  >
    <AIAlertCohorts />
  </FeatureToggle>
);

export const ECOAIAlertsCohorts = () => (
  <FeatureToggle
    feature={featureNames.APP_AI_ALERTS}
    fallback={<Navigate to={homePagePath} replace />}
    checkRole
  >
    <AIAlertCohorts />
  </FeatureToggle>
);

export const NexaDashboard = () => (
  <FeatureToggle
    feature={featureNames.NEXA}
    fallback={<Navigate to={homePagePath} replace />}
    checkRole
  >
    <Nexa />
  </FeatureToggle>
);

// using key to do a full remount
// to initiate a new jotai store and to have clean state between multiple temp dashboards of same type
export const RemountHoC = ({ Component }) => {
  const { id } = useParams();
  return <Component key={id} />;
};

export const NavigateWithParams = ({ to }) => {
  const params = useParams();
  const sanitizedParams = Object.fromEntries(
    Object.entries(params).map(([key, value]) => [key, value ?? null]),
  );
  return <Navigate to={generatePath(to, sanitizedParams)} replace />;
};
